import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Review, ReviewKind, ReviewViewModel } from '../models';
import { JsonPatchOperation } from '@oa/shared-core';
import { HttpHelperService } from '@oa/shared-core';

@Injectable()
export class ReviewService {
  private readonly _serviceName = 'ReviewService';
  constructor(private http: HttpClient, private httpHelper: HttpHelperService) {}

  get(id: number) {
    return this.http.get<Review>(`review/${id}`);
  }

  getAllAdmin() {
    return this.http.get<ReviewViewModel[]>('review').toPromise();
  }

  getAllAboutListing(listingId: number) {
    return this.http.get<ReviewViewModel[]>(`review/listing/${listingId}`);
  }

  getAllAboutMember(userAccountId: number) {
    return this.http.get<ReviewViewModel[]>(`review/aboutMember/${userAccountId}`);
  }

  getAllAuthoredByUser(userAccountId: number) {
    return this.http.get<ReviewViewModel[]>(`review/byUser/${userAccountId}`);
  }

  getAllAboutLandownerListings(userAccountId: number) {
    return this.http.get<ReviewViewModel[]>(`review/aboutLandownerListings/${userAccountId}`);
  }

  createReview(review: Review, kind: ReviewKind) {
    review.kind = kind;
    return this.http.post<Review>(`review/${kind === ReviewKind.FromLandowner ? 'byLandowner' : 'byMember'}`, review);
  }

  createReviewFromMember(review: Review) {
    return this.createReviewItem(review, ReviewKind.FromMember);
  }

  createReviewFromLandowner(review: Review) {
    return this.createReviewItem(review, ReviewKind.FromLandowner);
  }

  /**
   * Patches a single value for a review
   * @param id
   * @param path
   * @param value
   */
  public patchValue(id: number, path: string, value: any) {
    let patchOperation = { path, value, op: 'replace' } as JsonPatchOperation;

    return this.httpHelper.patch(`review/${id}`, [patchOperation]);
  }

  /**
   * Patches multiple values in a single patch operation
   * @param id
   * @param patchDocument
   */
  public patchValues(id: number, patchDocument: JsonPatchOperation[]) {

    console.log(`${this._serviceName}.updateReview`); 

    return this.httpHelper.patch(`review/${id}`, patchDocument);
  }

  /**
   * Updates a portion of a model (using JSON Patch Notation)
   * @param id    The id of the listing
   * @param path  The path of the poperty value being modified. (e.g. "./endDate" for TripReservationModel.endDate)
   * @param value The new value. Undefined or null will be treated as "remove" patch operations
   */
  // patch<K extends keyof Review>(id: number, path: K, value: Review[K]) {
  //   return this.http.patch(`review/${id}`, [
  //     {
  //       path,
  //       value,
  //       op: "replace"
  //     }
  //   ]);
  // }

  // /**
  //  * [patch description]
  //  * @param  {number} id             The id of the listing
  //  * @param  {string} patchDocument  The JSON Patch object (see https://tools.ietf.org/html/rfc6902)
  //  * @return {[type]}               Observable<Response> of the http.patch
  //  * TODO: use correct type via keyof for patch op when TS 2.1 is released.
  //  */
  // patch<K extends keyof Review>(id: number, patch: ReviewPatch<K>[]) {
  //   return this.http.patch(`review/${id}`, patch.map(p => ({ ...p, path: `/${p.path}` }))).map(x => x.json() as Review);
  // }

  /**
   * name
   */
  public ensureHasPropertySatisfying<T>(target: T, key: string & keyof T, predicate?: ((value: T[typeof key]) => boolean) | boolean) {
    if (!this.hasPropertySatisfying(target, key, predicate)) {
      throw Error(`Property '${key}' in target ${predicate ? 'invalid' : 'missing'}`);
    }
  }

  public hasPropertySatisfying<T extends {}>(target: T, key: string & keyof T, predicate?: ((value: T[typeof key]) => boolean) | boolean) {
    if (!predicate) {
      return key in target && target[key] !== undefined;
    }
    return typeof predicate === 'function' ? predicate(target[key]) : predicate;
  }

  private createReviewItem(review: Review, kind: ReviewKind) {
    const requiredKind = kind;
    this.ensureHasPropertySatisfying(review, 'kind', k => k === kind);
    const response = this.http.post<Review>(`review/${kind === ReviewKind.FromLandowner ? 'byLandowner' : 'byMember'}`, review);

    return response;
  }
}

export type ReviewPatch<K extends string & keyof Review> = { path: K; value: Review[K]; op: 'replace' };
